import { useState } from 'react';
import { Modal, TextInput } from '@carbon/react';
import type { AssetAlias } from 'types/api-responses';
import { checkAliasName } from './utils';
import styles from './add-alias-modal.module.scss';

interface AddAliasModalProps {
  open: boolean;
  onClose: () => void;
  /** Sends the name. Resolves to the server's message when it refuses it, or null once it is added. */
  onAdd: (name: string) => Promise<string | null>;
  isAdding: boolean;
  /** The aliases already on the record, for the duplicate check. */
  existingAliases: AssetAlias[];
  /** What the aliases are on, in the helper text: "tract" or "development area". */
  recordLabel: string;
  /** The record's number (T-… or U-…), shown as the dialog's label above its heading. */
  recordNumber: string;
}

/** An example tag for the field, by kind of record. */
const PLACEHOLDERS: Record<string, string> = {
  'tract': 'e.g. Gulf 7',
  'development area': 'e.g. Heron Pad',
};

/**
 * The Add alias dialog. Checks the name as the server will (empty, comma, length, and a duplicate of
 * this record's aliases, case and spacing aside) before it is sent, and disables Add alias while any of them
 * applies. If the server still refuses (two people adding the same alias at once), its message shows
 * under the field. On success the dialog closes.
 * @param {AddAliasModalProps} props - The record's aliases and the add handler.
 * @return {JSX.Element} The dialog.
 */
export function AddAliasModal({
  open,
  onClose,
  onAdd,
  isAdding,
  existingAliases,
  recordLabel,
  recordNumber,
}: AddAliasModalProps) {
  const [name, setName] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const check = checkAliasName(name, existingAliases, recordLabel);
  const error = serverError ?? check.error;
  const canSubmit = check.canAdd && !isAdding;

  /** Empties the form and tells the page to close the dialog. */
  const close = () => {
    setName('');
    setServerError(null);
    onClose();
  };

  /** Sends the name; closes on success, else shows the server's refusal. */
  const submit = async () => {
    if (!canSubmit) return;
    const refusal = await onAdd(name);
    if (refusal === null) {
      close();
    } else {
      setServerError(refusal);
    }
  };

  return (
    <Modal
      open={open}
      size="sm"
      modalLabel={recordNumber}
      modalHeading="Add alias"
      primaryButtonText="Add alias"
      secondaryButtonText="Cancel"
      primaryButtonDisabled={!canSubmit}
      preventCloseOnClickOutside
      shouldSubmitOnEnter
      onRequestClose={close}
      onRequestSubmit={submit}
    >
      <TextInput
        id="add-alias-name"
        className={styles.field}
        data-modal-primary-focus
        labelText="Alias / tag name"
        placeholder={PLACEHOLDERS[recordLabel]}
        helperText={`Shown as a pill on this ${recordLabel} and in the ${recordLabel} catalog`}
        value={name}
        invalid={error !== null}
        invalidText={error ?? ''}
        onChange={(event) => {
          setName(event.target.value);
          setServerError(null);
        }}
      />
    </Modal>
  );
}
